import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Tendly's OWN auth shell — deliberately NOT the shared `AuthLayout`/`AuthCard`.
 *
 * The generic kit framed auth as "a glass card centered in a split brand panel",
 * the shape every clone inherited (and which therefore read as a clone). Tendly's
 * identity is the maintenance ledger: a calm **service-record sheet**, not a
 * floating glass card. So this shell breaks the shared silhouette in two ways:
 *
 *   ┌──────────────────────────────────────┐
 *   │ ◳ Tendly                · eyebrow ·   │  ← service-tag bar: wrench chip +
 *   │ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─  │    wordmark + per-screen status tag
 *   │   ╭───────────╮                       │
 *   │   │ wrench/   │  Big headline.        │  ← compact maintenance mark beside
 *   │   │ asset svg │  Supporting line.     │    an editorial promise
 *   │   ╰───────────╯                       │
 *   │ ════════════════════════════════════  │  ← record divider (no card border)
 *   │   Email   [______________________]    │  ← form is FULL-BLEED on the bg,
 *   │   Pass    [______________________]    │    laid out like a maintenance
 *   │   [ Sign in ]                         │    record, no GlassSurface frame
 *   └──────────────────────────────────────┘
 *
 * No glass surface, no card chrome, no two-column split: the form lives directly
 * on the app background under a horizontal service-tag bar with an inline
 * maintenance mark. So Tendly looks structurally unlike listto's stacked
 * editorial column AND unlike the other products, even though it shares the
 * primitives (buttons, fields, SocialAuthButtons, disabled-until-ready). The
 * shell stays agnostic about the *form* — screens pour their fields in as
 * `children`.
 *
 * ── SCROLL CONTRACT (#13, same fix as the kit) ────────────────────────────
 * `AppBackground` is fixed/overflow-hidden and clips its children, so it is used
 * here as a pure backdrop sibling. The content lives in its OWN scroll
 * container: `min-h-[100dvh] overflow-y-auto` (never `h-dvh`/`overflow-hidden`),
 * with a `min-h-full` inner column, so a tall form (register) or a short
 * landscape viewport is always fully reachable by scrolling.
 */

/** Inline maintenance mark: a wrench tending a tagged asset with an "up to
 * date" seal. Theme-safe (currentColor + accent tokens). Decorative only. */
function MaintenanceMark(props: { class?: string }): JSX.Element {
  return (
    <svg
      class={props.class}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Asset tag */}
      <rect x="14" y="16" width="30" height="22" rx="4" fill="currentColor" opacity="0.1" />
      <rect x="14" y="16" width="30" height="22" rx="4" stroke="currentColor" stroke-width="2.2" />
      <circle cx="14" cy="16" r="3" stroke="currentColor" stroke-width="2.2" />
      <line
        x1="20"
        y1="24"
        x2="38"
        y2="24"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        opacity="0.5"
      />
      <line
        x1="20"
        y1="30"
        x2="32"
        y2="30"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        opacity="0.3"
      />
      {/* Wrench */}
      <g transform="rotate(-32 44 46)">
        <path
          d="M48 34a8 8 0 0 0-9.7 9.7l-13 13a4 4 0 1 0 5.6 5.6l13-13A8 8 0 0 0 57 44.1l-4.4 4.4a3.1 3.1 0 0 1-4.4-4.4l4.4-4.4A8 8 0 0 0 48 34Z"
          fill="currentColor"
          opacity="0.16"
        />
        <path
          d="M48 34a8 8 0 0 0-9.7 9.7l-13 13a4 4 0 1 0 5.6 5.6l13-13A8 8 0 0 0 57 44.1l-4.4 4.4a3.1 3.1 0 0 1-4.4-4.4l4.4-4.4A8 8 0 0 0 48 34Z"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linejoin="round"
        />
      </g>
      {/* "Up to date" seal */}
      <circle cx="46" cy="20" r="9" class="fill-accent" />
      <path
        d="M42 20.5l2.6 2.6 5-5"
        class="stroke-accent-foreground"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </svg>
  );
}

interface AuthSceneProps {
  /** Per-screen status tag rendered in the service-tag bar (e.g. "Welcome back"). */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The scene headline IS the page h1 for
   * Tendly's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the column. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** Hide the maintenance mark (transactional screens like verify/reset). */
  bare?: boolean;
  /** The form (fields + actions), rendered full-bleed below the brand band. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthScene(props: AuthSceneProps) {
  const headingClass =
    "text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-[1.75rem]";

  return (
    <>
      <AppBackground />

      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* Service-tag bar: wrench chip + wordmark + per-screen status tag. */}
          <header class="flex flex-col gap-5">
            <div class="flex items-center justify-between gap-3">
              <Link
                to="/"
                class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Tendly"
              >
                <span class="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                  <svg
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M16.9 5.1a3.8 3.8 0 0 0-4.62 4.62L5.6 16.4a1.9 1.9 0 0 0 2.7 2.7l6.68-6.68a3.8 3.8 0 0 0 4.62-4.62l-2.1 2.1a1.5 1.5 0 0 1-2.1-2.1l2.1-2.1ZM7.45 15.4a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="text-lg font-bold tracking-tight text-foreground">Tendly</span>
              </Link>
              <Show when={props.eyebrow}>
                <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/25">
                  <span class="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                  {props.eyebrow}
                </span>
              </Show>
            </div>

            {/* Dashed service-record rule under the tag bar. */}
            <div class="h-px w-full border-t border-dashed border-border/70" />

            <Show when={props.headline}>
              <div class="flex items-start gap-4">
                <Show when={!props.bare}>
                  <MaintenanceMark class="mt-0.5 h-12 w-12 shrink-0 text-accent" />
                </Show>
                <div class="space-y-2">
                  <Show
                    when={(props.headingLevel ?? "h1") === "h2"}
                    fallback={<h1 class={headingClass}>{props.headline}</h1>}
                  >
                    <h2 class={headingClass}>{props.headline}</h2>
                  </Show>
                  <Show when={props.tagline}>
                    <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                      {props.tagline}
                    </p>
                  </Show>
                </div>
              </div>
            </Show>
          </header>

          {/* Solid record divider (no card border). */}
          <div class="my-7 h-px w-full bg-border/70" />

          {/* Form lives full-bleed on the ambient background (no glass card). */}
          <div class="w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}
